import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../App.css';

import Loader from '../components/Loader';
import BrandLogo from '../components/BrandLogo';
import {
  AlertIcon,
  CheckIcon,
  CopyIcon,
  ChatIcon,
  MailIcon,
} from '../components/Icons';
import { BASE_URL } from '../App';
import {
  trackPurchase,
  trackOrderViewed,
  trackPaymentResultViewed,
  trackCodeRevealed,
  trackCodeCopied,
  trackRedeemLinkOpened,
  useTrackError,
} from '../utils/analytics';
import { readSelectedItem } from '../utils/portalConfig';
import { formatMoney, apiErrorMessage } from '../utils/format';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const RESULT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

const CopyableCode = ({ code, orderId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Clipboard is unavailable over plain HTTP and in some in-app browsers.
      // Select the text so the member can copy it by hand instead of getting
      // silent nothing.
      const range = document.createRange();
      const node = document.getElementById('reward-code');
      if (node) {
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      trackCodeCopied({ orderId, method: 'manual_select' });
      return;
    }
    trackCodeCopied({ orderId, method: 'clipboard' });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rewardBox">
      <code className="rewardCode" id="reward-code">
        {code}
      </code>
      <button type="button" className={cx('btn', 'btnSm', 'btnInk')} onClick={handleCopy}>
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const OrderDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // `/order/:orderId` is a real route, but this screen only ever read router
  // state — so that URL always rendered "No order ID found".
  const orderId = location.state?.orderId || params.orderId;

  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  useTrackError('order_details', error);

  useEffect(() => {
    let cancelled = false;

    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/partner/v2/get-order-details/${encodeURIComponent(orderId)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('portalToken')}`,
            },
          }
        );

        const data = await response.json().catch(() => ({}));
        if (cancelled) return;

        if (!response.ok || !data.order_id) {
          throw new Error(
            apiErrorMessage(data, 'We could not load this order.')
          );
        }

        // The plan the member came from carries the redemption copy the order
        // payload doesn't always include.
        const selectedPlan = readSelectedItem();

        setOrderDetails({
          ...data,
          plan_activation_message:
            selectedPlan?.plan_activation_message || data.plan_activation_message,
          coupon_type: selectedPlan?.coupon_type || data.coupon_type,
          steps_to_redeem_coupon:
            selectedPlan?.steps_to_redeem_coupon || data.steps_to_redeem_coupon,
          vendor_logo_url: selectedPlan?.vendor_logo_url,
          vendor_name: selectedPlan?.vendor_name,
        });

        trackOrderViewed({
          orderId: data.order_id,
          status: data.status,
          hasCode: Boolean(data.coupon_code),
        });
        trackPaymentResultViewed({
          orderId: data.order_id,
          status: RESULT_STATUS[data.status] || 'pending',
        });
        if (data.coupon_code) {
          // The cached plan is only trusted when it is this order's plan.
          const samePlan = selectedPlan?.plan_name && selectedPlan.plan_name === data.partner_plan;
          trackCodeRevealed({
            orderId: data.order_id,
            planId: samePlan ? selectedPlan.plan_id : null,
            deliveryType: (samePlan && selectedPlan.coupon_type) || data.coupon_type,
          });
        }
        // GA purchase + Ads conversion: once per order (see analytics.js), and
        // only for a paid order — /order/:id can open an unpaid one.
        if (!data.status || data.status === 'SUCCESS') trackPurchase(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading order details:', err);
        setError(err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    } else {
      setError('We could not find an order reference.');
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const supportCard = (
    <div className="card">
      <div className="cardTitle">Need support?</div>
      <p className="howBody" style={{ marginTop: 0 }}>
        Questions about this order? Send us the order ID and we'll pick it up.
      </p>
      <div className="supportRow">
        <a
          className={cx('btn', 'btnSm', 'btnSecondary')}
          href={`https://wa.me/919148477518?text=${encodeURIComponent(
            `Order support - Order ID: ${orderId || ''}`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ChatIcon />
          WhatsApp support
        </a>
        <a
          className={cx('btn', 'btnSm', 'btnSecondary')}
          href={`mailto:help@getfleek.app?subject=${encodeURIComponent(
            `Order Support - Order ID: ${orderId || ''}`
          )}`}
        >
          <MailIcon />
          Email support
        </a>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="page">
        <div className="statusWrap">
          <Loader />
          <p className="statusBody">Loading your order…</p>
        </div>
      </div>
    );
  }

  if (error || !orderDetails) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
            <h1 className="statusTitle">Order not available</h1>
            <p className="statusBody">{error || 'No order details found.'}</p>
            {/* This screen used to have its only navigation commented out,
                which stranded anyone whose order lookup failed. */}
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={() => navigate('/marketplace')}
              >
                Back to marketplace
              </button>
            </div>
          </div>
          {orderId && <div className="statusMeta">Order ID: {orderId}</div>}
          <div style={{ marginTop: 16, textAlign: 'left' }}>{supportCard}</div>
        </div>
      </div>
    );
  }

  const currencyLabel = (orderDetails.currency || 'INR').toUpperCase();
  const activationUrl = orderDetails.activation_url;

  return (
    <div className="page">
      <div className="detailShell">
        <div className="detailGrid">
          <div>
            <div className="card">
              <div className="detailHero">
                <div className="statusIcon statusIconOk" style={{ margin: 0 }} aria-hidden="true">
                  <CheckIcon width="28" height="28" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h1 className="detailBrand">Order confirmed</h1>
                  <div className="detailPlanName">Order ID: {orderDetails.order_id}</div>
                </div>
              </div>
            </div>

            {orderDetails.coupon_code && (
              <div className="card">
                <div className="cardTitle">Your code</div>
                <CopyableCode code={orderDetails.coupon_code} orderId={orderDetails.order_id} />

                {orderDetails.plan_activation_message && (
                  <p className="fieldHint">{orderDetails.plan_activation_message}</p>
                )}

                {/* Only offer the button when there is somewhere to go — it
                    used to render for any activation message and open
                    about:blank when activation_url was absent. */}
                {activationUrl && (
                  <a
                    className={cx('btn', 'btnLg', 'btnBrand', 'btnBlock')}
                    style={{ marginTop: 16 }}
                    href={activationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackRedeemLinkOpened({
                        orderId: orderDetails.order_id,
                        brand: orderDetails.vendor_name,
                      })
                    }
                  >
                    Activate now
                  </a>
                )}
              </div>
            )}

            {orderDetails.steps_to_redeem_coupon && (
              <div className="card">
                <div className="cardTitle">How to redeem</div>
                <p className="howBody" style={{ marginTop: 0, whiteSpace: 'pre-line' }}>
                  {orderDetails.steps_to_redeem_coupon}
                </p>
              </div>
            )}

            {supportCard}
          </div>

          <aside className="detailAside">
            <div className="card">
              <div className="cardTitle">Order summary</div>

              {orderDetails.vendor_name && (
                <div className="offerTop" style={{ marginBottom: 16 }}>
                  <BrandLogo
                    className="offerLogo"
                    src={orderDetails.vendor_logo_url}
                    name={orderDetails.vendor_name}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="bundleItemName">{orderDetails.vendor_name}</div>
                    <div className="bundleItemMeta">{orderDetails.partner_plan}</div>
                  </div>
                </div>
              )}

              {orderDetails.partner && (
                <div className="priceRow">
                  <span>Partner</span>
                  <span className="priceValue">{orderDetails.partner}</span>
                </div>
              )}

              {orderDetails.partner_plan && !orderDetails.vendor_name && (
                <div className="priceRow">
                  <span>Plan</span>
                  <span className="priceValue">{orderDetails.partner_plan}</span>
                </div>
              )}

              {orderDetails.created_at && (
                <div className="priceRow">
                  <span>Date</span>
                  <span className="priceValue">
                    {new Date(orderDetails.created_at).toLocaleString()}
                  </span>
                </div>
              )}

              {orderDetails.user_details?.email && (
                <div className="priceRow">
                  <span>Email</span>
                  <span className="priceValue">{orderDetails.user_details.email}</span>
                </div>
              )}

              <div className="priceRow priceRowTotal">
                <span>Paid ({currencyLabel})</span>
                <span className="priceTotal">
                  {formatMoney(orderDetails.order_amount, orderDetails.currency)}
                </span>
              </div>
            </div>

            <button
              type="button"
              className={cx('btn', 'btnMd', 'btnSecondary', 'btnBlock')}
              style={{ marginTop: 16 }}
              onClick={() => navigate('/marketplace')}
            >
              Back to marketplace
            </button>
          </aside>
        </div>

        <div className="pageFoot" />
      </div>
    </div>
  );
};

export default OrderDetails;
